/**
 * Het scherm van de leerling: de stappen van een les, achter elkaar.
 *
 *   welkom → (woorden →) lezen → meerkeuze → uitslag → open vragen → klaar
 *
 * en daarnaast: terugkijken, een tekst die de leerling al eerder las. De
 * woordenoefening (stap 29, stappen/Woorden.js) kan vanaf het welkom, als de
 * woordenlijst op zijn niveau klaar is; daarna gaat hij lezen.
 *
 * Dit bestand is de stappenmachine en houdt vast wat meer dan één stap
 * nodig heeft: de les, de alinea's (die ook naast de toets staan), de vragen
 * en de antwoorden. Elke stap staat in stappen/, de bouwstenen in
 * onderdelen/, de hulp van Anna Maria en het voorlezen in hooks/, en de
 * aanroepen van de server in api/leerling.js.
 *
 * Herladen of opnieuw inloggen: de server weet waar de leerling is (de
 * status van zijn les). Ook dan begint hij bij het welkom, maar dat zegt nu
 * dat hij verder gaat (`verder`): bij `lezen` "Je gaat verder met het lezen
 * van ...", en de knop brengt hem terug bij de tekst; bij `toets_open` gaat
 * hij verder met de open vragen, met de tekst ernaast. Tot 1 november ging
 * hij meteen door naar de tekst of de vragen, zonder welkom. (Bij de open
 * vragen ging het ooit mis: de vragen en de tekst werden niet opnieuw
 * opgehaald, en de leerling zat vast in een lege toets, fout 6.) Heeft de
 * les geen open vragen meer, dan wordt hij bij herladen meteen afgerond,
 * zonder welkom; anders zat de leerling vast voor een knop die nooit aan
 * ging. Wie herlaadt tijdens de meerkeuzevragen, staat nog op `lezen` (de
 * status verandert pas bij versturen) en begint weer bij de tekst; hij
 * krijgt dezelfde vragen terug, want de toets ligt vast zodra hij ze ophaalt.
 *
 * In een groepsles (stap 23, `les.groepsles`) leest de leerling alleen zijn
 * deel en is er geen toets: na het lezen is hij klaar (rondLezenAf), en het
 * scherm zegt met wie hij in een groepje zit.
 *
 * Wat de leerkracht per leerling instelt (voorlezen, de moedertaal, en of de
 * vertaling kan worden voorgelezen) haalt het scherm opnieuw op als de
 * leerling begint met lezen, als hij terugkomt in zijn venster, en tijdens
 * het lezen elke minuut (verversInstellingen). Vroeger las het die alleen
 * bij het openen, en zag een leerling een wijziging in Groepen pas na
 * herladen.
 */
import React, { useEffect, useState } from 'react';
import { BookOpen } from 'lucide-react';
import { leerlingApi as api } from '../../api/leerling';
import { Kop } from '../../components/Kop';
import { niveausVan, voegKoppenSamen } from './alineas';
import { useHulp, WEER_TERUG, WELKOM } from './hooks/useHulp';
import { useIllustratie } from './hooks/useIllustratie';
import { useVoorlezen, vertalingSleutel } from './hooks/useVoorlezen';
import { GelezenTeksten } from './onderdelen/GelezenTeksten';
import { Klaar } from './stappen/Klaar';
import { Lezen } from './stappen/Lezen';
import { Meerkeuze } from './stappen/Meerkeuze';
import { MeerkeuzeUitslag, eersteFouteVraag } from './stappen/MeerkeuzeUitslag';
import { OpenVragen } from './stappen/OpenVragen';
import { Terugkijken } from './stappen/Terugkijken';
import { Welkom } from './stappen/Welkom';
import { Woorden } from './stappen/Woorden';

/** Zo vaak (ms) haalt het scherm tijdens het lezen de instellingen van de leerkracht opnieuw op. */
export const VERVERS_MS = 60_000;

function LeerlingFlow({ user, onLogout }) {
  const [stap, setStap] = useState('welkom'); // welkom, woorden, lezen, meerkeuze, uitslag, open, klaar, terugkijken
  const [les, setLes] = useState(null);
  const [verder, setVerder] = useState(null); // null: hij begint nog; 'lezen' of 'vragen': hij was al begonnen
  const [fout, setFout] = useState('');

  // de tekst
  const [alineas, setAlineas] = useState([]);
  const [niveaus, setNiveaus] = useState({}); // per alinea het niveau waarop hij nu staat
  const [alineasLaden, setAlineasLaden] = useState(false);

  // de toets
  const [mcVragen, setMcVragen] = useState([]);
  const [mcAntwoorden, setMcAntwoorden] = useState({});
  const [mcUitslag, setMcUitslag] = useState(null);
  const [gekozenVraag, setGekozenVraag] = useState(null); // in de uitslag: welke passage geel is
  const [toetsLaden, setToetsLaden] = useState(false);
  const [openVragen, setOpenVragen] = useState([]);
  const [openAntwoorden, setOpenAntwoorden] = useState({});
  const [feedback, setFeedback] = useState({});
  const [versturen, setVersturen] = useState(false); // antwoorden onderweg: de knop staat dan uit

  // eerder gelezen teksten
  const [gelezenTeksten, setGelezenTeksten] = useState([]);
  const [lijstOpen, setLijstOpen] = useState(false);
  const [terug, setTerug] = useState(null); // de tekst die wordt teruggekeken

  // De moedertaal zoals de server hem nu kent (die van het inloggen kan intussen veranderd zijn).
  const moedertaal = les && les.moedertaal !== undefined ? les.moedertaal : user.moedertaal;
  const hulp = useHulp({ gebruiker: { ...user, moedertaal }, leerlingLesId: les?.session_id, setAlineas, niveaus, setNiveaus });
  const voorlezen = useVoorlezen(hulp.setBericht, les?.session_id, hulp.meldBijVertaling);
  // Een andere (of geen) vertaling in het vak van Anna Maria: de vorige niet meer voorlezen.
  useEffect(() => {
    voorlezen.stopVertaling(hulp.vertaling ? vertalingSleutel(hulp.vertaling) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen als de vertaling verandert
  }, [hulp.vertaling]);
  const illustratie = useIllustratie(les?.illustration_url);

  const laadAlineas = async (leerlingLesId) => {
    const samen = voegKoppenSamen(await api.haalAlineas(leerlingLesId));
    setAlineas(samen);
    setNiveaus(niveausVan(samen));
  };

  // Bij het openen: de les en waar de leerling was, en de teksten die al gelezen zijn.
  useEffect(() => {
    const begin = async () => {
      try {
        const actief = await api.haalActieveLes();
        if (actief) {
          setLes(actief);
          if (actief.status === 'lezen') {
            setVerder('lezen');   // eerst het welkom: "Je gaat verder met het lezen van ..."
          } else if (actief.status === 'afgerond' && actief.groepsles) {
            setStap('klaar');   // klaar met lezen in een groepsles die nog aan staat: met wie hij in een groepje zit
          } else if (actief.status === 'toets_open') {
            const open = await api.haalOpenVragen(actief.session_id);
            if (open.length > 0) {
              setOpenVragen(open);
              setVerder('vragen');   // eerst het welkom: "Je gaat verder met de vragen ..."
            } else {
              await gaNaarOpenVragen(actief.session_id, open);   // geen open vragen: de les is nu afgerond
            }
          }
        }
        setGelezenTeksten(await api.haalGelezenTeksten());
      } catch (err) {
        setFout(err.message);
      }
    };
    begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, []);

  /**
   * De instellingen van de leerkracht opnieuw ophalen (voorlezen, de
   * moedertaal, de vertaling voorlezen), zonder de stap te veranderen. Gaat
   * het mis, dan blijft het zoals het was; het volgende moment opnieuw.
   */
  const verversInstellingen = async () => {
    try {
      const actief = await api.haalActieveLes();
      // ook de groepsles: de leerkracht kan een kind verplaatsen (een ander groepje, of een deel zolang het niet begon)
      setLes(huidig => (huidig && actief && actief.session_id === huidig.session_id
        ? { ...huidig, voorlezen: actief.voorlezen, vertaling_voorlezen: actief.vertaling_voorlezen,
          moedertaal: actief.moedertaal, groepsles: actief.groepsles, woorden: actief.woorden }
        : huidig));
    } catch (err) {
      // stil: dit is alleen bijwerken
    }
  };

  // Tijdens het lezen: bij terugkomen in het venster, en elke minuut.
  useEffect(() => {
    if (stap !== 'lezen') return undefined;
    const bijTerugkomen = () => { if (document.visibilityState !== 'hidden') verversInstellingen(); };
    window.addEventListener('focus', bijTerugkomen);
    document.addEventListener('visibilitychange', bijTerugkomen);
    const klok = setInterval(verversInstellingen, VERVERS_MS);
    return () => {
      window.removeEventListener('focus', bijTerugkomen);
      document.removeEventListener('visibilitychange', bijTerugkomen);
      clearInterval(klok);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen als de stap verandert
  }, [stap]);

  /** Elke stap die de server iets vraagt: een fout komt boven het scherm. */
  const probeer = async (doe) => {
    try {
      await doe();
    } catch (err) {
      setFout(err.message);
    }
  };

  const beginMetLezen = async () => {
    if (!les) return;
    setAlineasLaden(true);
    await probeer(async () => {
      await api.beginMetLezen(les.session_id);
      await Promise.all([laadAlineas(les.session_id), verversInstellingen()]);
      setStap('lezen');
      setVerder('lezen');   // komt hij terug bij het welkom (na Gelezen teksten), dan gaat hij verder
      hulp.setBericht(WELKOM);
    });
    setAlineasLaden(false);
  };

  /** Vanaf het welkom terug naar waar hij was: de tekst, of de open vragen (met de tekst ernaast). */
  const gaVerder = async () => {
    if (!les) return;
    setAlineasLaden(true);
    await probeer(async () => {
      await Promise.all([laadAlineas(les.session_id), verversInstellingen()]);
      if (verder === 'vragen') {
        await gaNaarOpenVragen(les.session_id, openVragen);
      } else {
        setStap('lezen');
        hulp.setBericht(WEER_TERUG);
      }
    });
    setAlineasLaden(false);
  };

  // Een les kan nul meerkeuze- of nul open vragen hebben (dat kiest de
  // leerkracht). Dan slaat de leerling dat deel over; zonder open vragen is de
  // les na de meerkeuzevragen afgerond.
  const naarToets = async () => {
    setToetsLaden(true);
    await probeer(async () => {
      const vragen = await api.haalMeerkeuzevragen(les.session_id);
      // De tekst naast de vragen is die waar de toets over gaat: zonder wat de
      // leerling vereenvoudigde, zodat de passage bij een vraag erin staat (stap 25c).
      await laadAlineas(les.session_id);
      setMcVragen(vragen);
      if (vragen.length > 0) setStap('meerkeuze');
      else await gaNaarOpenVragen(les.session_id);
    });
    setToetsLaden(false);
  };

  /** Naar de open vragen; zijn er geen, dan is de les nu afgerond. `vragen` als ze er al zijn. */
  async function gaNaarOpenVragen(leerlingLesId, vragen) {
    const open = vragen || await api.haalOpenVragen(leerlingLesId);
    setOpenVragen(open);
    if (open.length > 0) {
      setStap('open');
      setVerder('vragen');
    } else {
      await api.verstuurOpenVragen(leerlingLesId, []);
      setFeedback({});
      setStap('klaar');
    }
  }

  // Na het nakijken alvast de open vragen, zodat de knop onder de uitslag
  // kan zeggen of er nog iets komt ("Les afronden" als er geen zijn).
  const verstuurMeerkeuze = async () => {
    setVersturen(true);
    await probeer(async () => {
      const antwoorden = mcVragen.map(v => ({ question_id: v.id, answer: mcAntwoorden[v.id] || '' }));
      const uitslag = await api.verstuurMeerkeuze(les.session_id, antwoorden);
      setMcUitslag(uitslag);
      setGekozenVraag(eersteFouteVraag(uitslag));
      setOpenVragen(await api.haalOpenVragen(les.session_id));
      setStap('uitslag');
    });
    setVersturen(false);
  };

  const naarOpenVragen = async () => {
    setVersturen(true);   // zonder open vragen rondt dit de les af: niet twee keer
    await probeer(() => gaNaarOpenVragen(les.session_id, openVragen));
    setVersturen(false);
  };

  const verstuurOpenVragen = async () => {
    setVersturen(true);
    await probeer(async () => {
      const antwoorden = openVragen.map(v => ({ question_id: v.id, answer: openAntwoorden[v.id] || '' }));
      const terugkoppeling = await api.verstuurOpenVragen(les.session_id, antwoorden);
      const perVraag = {};
      terugkoppeling.forEach(f => { perVraag[f.question_id] = f.feedback; });
      setFeedback(perVraag);
      setStap('klaar');
    });
    setVersturen(false);
  };

  // Een groepsles: geen toets; na het lezen is het deel in de app klaar.
  const klaarMetLezen = async () => {
    setToetsLaden(true);
    await probeer(async () => {
      await api.rondLezenAf(les.session_id);
      setStap('klaar');
    });
    setToetsLaden(false);
  };

  const bekijkGelezen = (leerlingLesId) => {
    setLijstOpen(false);
    return probeer(async () => {
      setTerug(await api.haalGelezenTekst(leerlingLesId));
      setStap('terugkijken');
    });
  };

  const titel = les?.text_titel;

  return (
    <div style={{ background: 'var(--grond-leerling)', minHeight: '100vh' }}>
      <Kop
        compact
        naam={user.naam}
        onLogout={onLogout}
        extra={gelezenTeksten.length > 0 && (
          <button className="btn btn-secondary" onClick={() => setLijstOpen(true)} style={{ padding: '8px 16px' }}>
            <BookOpen size={16} /> Gelezen teksten
          </button>
        )}
      />

      {lijstOpen && <GelezenTeksten teksten={gelezenTeksten} onBekijk={bekijkGelezen} onSluit={() => setLijstOpen(false)} />}

      {fout && (
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 24px' }}>
          <div className="alert alert-error">{fout}</div>
        </div>
      )}

      {stap === 'welkom' && (
        <Welkom naam={user.naam} les={les} verder={verder} bezig={alineasLaden}
          onBegin={verder ? gaVerder : beginMetLezen} onOefen={() => setStap('woorden')} />
      )}

      {stap === 'woorden' && les && (
        <Woorden les={les} bezigMetLezen={alineasLaden} onKlaar={verder ? gaVerder : beginMetLezen}
          onTerug={() => { setStap('welkom'); verversInstellingen(); }} />
      )}

      {stap === 'terugkijken' && terug && (
        <Terugkijken gelezen={terug} onTerug={() => { setTerug(null); setStap('welkom'); }} />
      )}

      {stap === 'lezen' && (
        <Lezen titel={titel} alineas={alineas} illustratie={illustratie} moedertaal={moedertaal}
          hulp={hulp} voorlezen={voorlezen} magVoorlezen={!!les?.voorlezen}
          magVertalingVoorlezen={!!les?.vertaling_voorlezen} toetsLaadt={toetsLaden} onNaarToets={naarToets}
          groepsles={les?.groepsles} onKlaar={klaarMetLezen} />
      )}

      {stap === 'meerkeuze' && (
        <Meerkeuze titel={titel} alineas={alineas} vragen={mcVragen} antwoorden={mcAntwoorden}
          onKies={(id, letter) => setMcAntwoorden(prev => ({ ...prev, [id]: letter }))} onVerstuur={verstuurMeerkeuze}
          bezig={versturen} />
      )}

      {stap === 'uitslag' && mcUitslag && (
        <MeerkeuzeUitslag titel={titel} alineas={alineas} vragen={mcVragen} antwoorden={mcAntwoorden} uitslag={mcUitslag}
          gekozen={gekozenVraag} onKiesVraag={setGekozenVraag} heeftOpenVragen={openVragen.length > 0}
          onVerder={naarOpenVragen} bezig={versturen} />
      )}

      {stap === 'open' && (
        <OpenVragen titel={titel} alineas={alineas} vragen={openVragen} antwoorden={openAntwoorden}
          onTyp={(id, tekst) => setOpenAntwoorden(prev => ({ ...prev, [id]: tekst }))}
          uitslagDeel1={mcUitslag} onVerstuur={verstuurOpenVragen} bezig={versturen} />
      )}

      {stap === 'klaar' && (
        <Klaar naam={user.naam} vragen={openVragen} antwoorden={openAntwoorden} feedback={feedback} groepsles={les?.groepsles} />
      )}
    </div>
  );
}

export default LeerlingFlow;
